// src/ResearchLabForm.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Form, Button, Row, Col, Alert, Table } from 'react-bootstrap';

function ResearchLabForm() {
    const [labId, setLabId] = useState('');
    const [labName, setLabName] = useState('');
    const [labDescription, setLabDescription] = useState('');
    const [labHead, setLabHead] = useState('');
    const [users, setUsers] = useState([]);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');
    const [isUpdateMode, setIsUpdateMode] = useState(false);
    const [labs, setLabs] = useState([]);
    const [isViewing, setIsViewing] = useState(false);
    const [labData, setLabData] = useState({
        labId: '',
        labName: '',
        labDesc: '',
        labHead: ''
    });

    // Initial lab data for resetting the form
    const initialLabData = {
        labId: '',
        labName: '',
        labDesc: '',
        labHead: ''
    };

    // Fetch users for Lab Head dropdown
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users`);
                setUsers(response.data);
            } catch (error) {
                console.error('Error fetching users:', error);
                setAlertMessage('Error fetching users for Lab Head.');
                setAlertType('danger');
            }
        };
        fetchUsers();
    }, []);

    // Fetch labs when viewing
    const handleView = async () => {
        if (!isViewing) {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/researchlabs`);
                setLabs(response.data);
                setIsViewing(true);
            } catch (error) {
                setAlertMessage('Error fetching research labs. Please try again.');
                setAlertType('danger');
                console.error('View Error:', error);
            }
        } else {
            setIsViewing(false);
        }
    };

    // Reset the form fields
    const resetForm = () => {
        setLabId('');
        setLabName('');
        setLabDescription('');
        setLabHead('');
        setIsUpdateMode(false);
        setAlertMessage('');
    };

    // Handle creating a research lab
    const handleCreate = async () => {
        const researchLabData = {
            labName,
            labId,
            labDesc: labDescription,
            labHead: { userId: labHead },
        };

        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/researchlabs`, researchLabData);
            setAlertMessage('Research Lab has been successfully added!');
            setAlertType('success');

            setTimeout(() => {
                resetForm(); // Reset the form after 2 seconds
            }, 2000);
            
        } catch (error) {
            setAlertMessage('Error creating research lab. Please try again.');
            setAlertType('danger');
            setTimeout(() => {
                setAlertMessage(''); // Clear error message after 3 seconds
            }, 3000);
            console.error('Create Error:', error);
        }
    };

    // Handle deleting a research lab
    const handleDelete = async () => {
        try {
            const response = await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/researchlabs/${labId}`);
            setAlertMessage(response.data);
            setAlertType('success');
            
            setTimeout(() => {
                resetForm(); // Reset the form after deletion
            }, 2000);
        } catch (error) {
            if (error.response && error.response.data) {
                setAlertMessage(error.response.data);
                setAlertType('danger');
                setTimeout(() => {
                    setAlertMessage(''); // Clear error message after 3 seconds
                }, 3000);
            } else {
                setAlertMessage('Error deleting research lab. Please try again.');
                setAlertType('danger');
                setTimeout(() => {
                    setAlertMessage(''); // Clear error message after 3 seconds
                }, 3000);
            }
            console.error('Delete Error:', error);
        }
    };

    // Handle updating research lab
    const handleUpdate = async () => {
        if (isUpdateMode) {
            const updatedLabData = {
                labName,
                labId,
                labDesc: labDescription,
                labHead: { userId: labHead },
            };

            try {
                await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/researchlabs/${labId}`, updatedLabData);
                setAlertMessage('Research lab updated successfully!');
                setAlertType('success');
                
                setTimeout(() => {
                    resetForm(); // Reset the form after updating
                }, 2000);
            } catch (error) {
                setAlertMessage('Error updating research lab. Please try again.');
                setAlertType('danger');
                setTimeout(() => {
                    setAlertMessage(''); // Clear error message after 3 seconds
                }, 3000);
                console.error('Update Error:', error);
            }
        } else {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/researchlabs/${labId}`);
                const labData = response.data;
                setLabName(labData.labName);
                setLabDescription(labData.labDesc);
                setLabHead(labData.labHead?.userId || '');
                setIsUpdateMode(true);
                setAlertMessage('Edit the fields and click Update again to save changes.');
                setAlertType('info');
            } catch (error) {
                setAlertMessage('Error fetching research lab details. Please try again.');
                setAlertType('danger');
                setTimeout(() => {
                    setAlertMessage(''); // Clear error message after 3 seconds
                }, 3000);
                console.error('Fetch Lab Error:', error);
            }
        }
    };

    return (
        <Container fluid className="d-flex justify-content-center align-items-start" style={{ minHeight: '100vh' }}>
            {!isViewing ? (
                <div className="border p-4 rounded" style={{ width: '100%', maxWidth: '500px', boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.1)', marginTop: '60px' }}>
                    <h2 className="text-center mb-4" style={{ fontSize: '30px', color: 'navy' }}>Manage Research Centre</h2>

                    {alertMessage && <Alert variant={alertType}>{alertMessage}</Alert>}

                    <Form>
                        {/* Lab ID Field */}
                        <Form.Group controlId="labId" className="mb-3">
                            <Form.Label>Centre ID</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter Centre ID"
                                value={labId}
                                onChange={(e) => setLabId(e.target.value)}
                            />
                        </Form.Group>

                        {/* Lab Name Field */}
                        <Form.Group controlId="labName" className="mb-3">
                            <Form.Label>Centre Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter Centre Name"
                                value={labName}
                                onChange={(e) => setLabName(e.target.value)}
                            />
                        </Form.Group>

                        {/* Lab Description Field */}
                        <Form.Group controlId="labDescription" className="mb-3">
                            <Form.Label>Centre Description</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter Centre Description"
                                value={labDescription}
                                onChange={(e) => setLabDescription(e.target.value)}
                            />
                        </Form.Group>

                        {/* Lab Head Field */}
                        <Form.Group controlId="labHead" className="mb-3">
                            <Form.Label>Centre Head</Form.Label>
                            <Form.Select value={labHead} onChange={(e) => setLabHead(e.target.value)}>
                                <option value="">Select Centre Head</option>
                                {users.map((user) => (
                                    <option key={user.userId} value={user.userId}>
                                        {user.username}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {/* Action Buttons */}
                        <Row className="mt-4">
                            <Col className="d-flex justify-content-around">
                                <Button variant="primary" style={{ width: '100px', backgroundColor: 'navy', color: 'white' }} onClick={handleCreate}>Create</Button>
                                <Button variant="primary" style={{ width: '100px', backgroundColor: 'navy', color: 'white' }} onClick={handleDelete}>Remove</Button>
                                <Button variant="primary" style={{ width: '100px', backgroundColor: 'navy', color: 'white' }} onClick={handleUpdate}>{isUpdateMode ? 'Save Update' : 'Update'}</Button>
                                <Button variant="primary" style={{ width: '100px', backgroundColor: 'navy', color: 'white' }} onClick={handleView}>View</Button>
                            </Col>
                        </Row>
                    </Form>
                </div>
            ) : (
                <div className="d-flex flex-row" style={{ width: '100%' }}>
                    <div className="border p-4 rounded" style={{ width: '35%', boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.1)', marginTop: '80px' }}>
                        <h2 className="text-center mb-4" style={{ fontSize: '30px', color: 'navy' }}>Manage Research Centre</h2>

                        {alertMessage && <Alert variant={alertType}>{alertMessage}</Alert>}

                        <Form>
                            {/* Lab ID Field */}
                            <Form.Group controlId="labId" className="mb-3">
                                <Form.Label>Centre ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Centre ID"
                                    value={labId}
                                    onChange={(e) => setLabId(e.target.value)}
                                />
                            </Form.Group>

                            {/* Lab Name Field */}
                            <Form.Group controlId="labName" className="mb-3">
                                <Form.Label>Centre Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Centre Name"
                                    value={labName}
                                    onChange={(e) => setLabName(e.target.value)}
                                />
                            </Form.Group>

                            {/* Lab Description Field */}
                            <Form.Group controlId="labDescription" className="mb-3">
                                <Form.Label>Centre Description</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Centre Description"
                                    value={labDescription}
                                    onChange={(e) => setLabDescription(e.target.value)}
                                />
                            </Form.Group>

                            {/* Lab Head Field */}
                            <Form.Group controlId="labHead" className="mb-3">
                                <Form.Label>Centre Head</Form.Label>
                                <Form.Select value={labHead} onChange={(e) => setLabHead(e.target.value)}>
                                    <option value="">Select Centre Head</option>
                                    {users.map((user) => (
                                        <option key={user.userId} value={user.userId}>
                                            {user.username}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            {/* Action Buttons */}
                            <Row className="mt-4">
                                <Col className="d-flex justify-content-around">
                                    <Button variant="primary" style={{ width: '100px', backgroundColor: 'navy', color: 'white' }} onClick={handleCreate}>Create</Button>
                                    <Button variant="primary" style={{ width: '100px', backgroundColor: 'navy', color: 'white' }} onClick={handleDelete}>Remove</Button>
                                    <Button variant="primary" style={{ width: '100px', backgroundColor: 'navy', color: 'white' }} onClick={handleUpdate}>{isUpdateMode ? 'Save Update' : 'Update'}</Button>
                                    <Button variant="primary" style={{ width: '100px', backgroundColor: 'navy', color: 'white' }} onClick={handleView}>View</Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>

                    <div style={{ maxHeight: '500px', overflowY: 'auto' }} className='ps-4 mt-5'>
    <h4 className="text-center mb-3" style={{ color: 'navy' }}>Research Centre List</h4>
  <table className="table table-striped table-bordered table-hover rounded-5">
<thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
                                <tr>
                                    <th className='bg-secondary'>Centre ID</th>
                                    <th className='bg-secondary'>Centre Name</th>
                                    <th className='bg-secondary'>Centre Description</th>
                                    <th className='bg-secondary'>Centre Head</th>
                                </tr>
                            </thead>
                            <tbody>
                                {labs.map((lab) => (
                                    <tr key={lab.labId}>
                                        <td>{lab.labId}</td>
                                        <td>{lab.labName}</td>
                                        <td>{lab.labDesc}</td>
                                        <td>{lab.labHead?.username}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </Container>
    );
}

export default ResearchLabForm;
